import { useEffect, useMemo, useState, useRef } from 'react'
import { useSelector } from 'react-redux';
import { Textarea, TextInput } from '@mantine/core';
import { store } from '../../../redux/store';
import { sendFeedbackForm } from '../../../services/api'
import useTranslation from '../../../hooks/useTranslation';
import { selectConfigurationId, selectDesignId, selectLinkInfo, selectLocaleFullCode, selectProductCode } from '../../../redux/appSlice';
import { getProductIDForFeedback } from '../../../utils/url-utils';

// import { useInterval, useProjectCountry } from '../../utils/customHooks/customHooks'
// import Icon from '../Icon'
import { Close, Success } from '../../icons/Icons';
import SmileySelector from '../SmileySelector/SmileySelector'

import './FeedbackForms.scss'

const FeedbackSources = {
  FullDialog: 'full-dialog',
  CompactDialog: 'compact-dialog',
  ErrorDialog: 'error-dialog',
}

const AUTO_CLOSE_SECONDS = 3

function isValidEmail(email = '', required) {
  if (!email) return !required
  // This should be enough if we assume that the users don't input ridiculous values
  const isValid = (/^(([^<>()\[\]\\.,;:\s@"]+(\.[^<>()\[\]\\.,;:\s@"]+)*)|(".+"))@((\[[0-9]{1,3}\.[0-9]{1,3}\.[0-9]{1,3}\.[0-9]{1,3}])|(([a-zA-Z\-0-9]+\.)+[a-zA-Z]{2,}))$/.test(email))
  return isValid
}

function useInterval(callback, delay) {
  const savedCallback = useRef();

  // Remember the latest callback.
  useEffect(() => {
    savedCallback.current = callback;
  }, [callback]);

  // Set up the interval.
  useEffect(() => {
    function tick() {
      savedCallback.current();
    }
    if (delay !== null) {
      let id = setInterval(tick, delay);
      return () => clearInterval(id);
    }
  }, [delay]);
}


function SuccessView(props) {
  const [seconds, setSeconds] = useState(props.autoCloseSeconds)
  const t = useTranslation()

  useInterval(() => {
    if (seconds <= 0) return
    setSeconds((prev) => prev - 1)
  }, 1000)

  useEffect(() => {
    const timeoutId = setTimeout(() => {
      props.onClose?.()
    }, (props.autoCloseSeconds + 1) * 1000) // "plus 1" so the zero is displayed in the UI for a second

    return () => {
      clearTimeout(timeoutId)
    }
  }, [])

  return (
    <div className="SuccessView">
      <p className="heading">{t('ui-feedback-success-thank-you')}</p>
      <Success className="success-icon" size={100} />
      <p className="closes-in-text">
        {t('ui-feedback-success-close-in-seconds-1')} {seconds}{' '}
        {t('ui-feedback-success-close-in-seconds-2')}
      </p>
    </div>
  )
}

export function CompactFeedbackForm(props) {
  const { className = '', onClose, executeRecaptcha } = props

  const {
    submit,
    email,
    setEmail,
    emailValid,
    message,
    setMessage,
    smileySelection,
    setSmileySelection,
    canSubmit,
    successfullySubmitted,
  } = useFeedbackDialogAPI(
    {
      executeRecaptcha: executeRecaptcha
    },
    { numberRating: true, message: true, email: true }
  )

  const t = useTranslation()

  async function handleSubmit() {
    try {
      await submit()
    } catch (err) {
      console.log(err)
    }
  }

  return (
    <div className={`CompactFeedbackForm ${className}`}>
      <div className="close-button-container">
        <div className="close-icon" onClick={onClose}>
          <Close />
        </div>
      </div>
      {!successfullySubmitted ? (
        <>
          <h2 className="heading">{t('ui-feedback-compact-dialog-heading')}</h2>
          <SmileySelector selection={smileySelection} setSelection={setSmileySelection} />
          <Textarea
            label={t('ui-feedback-give-us-feedback')}
            placeholder={t('ui-feedback-give-us-feedback')}
            value={message}
            onChange={(e) => setMessage(e.currentTarget.value)}
            className="form-section"
            minRows={3}

          />
          <TextInput
            label={t('ui-feedback-email-label')}
            placeholder={t('ui-feedback-email-placeholder')}
            error={!emailValid && t('ui-contact-error-email')}
            value={email}
            onChange={(e) => {
              setEmail(e.currentTarget.value)
            }}
            className="form-section half-width"
          />
          <p className="PrivacyStatement" data-testid="PrivacyStatement">
            {t('ui-contact-privacy-statement-1')}
            <a
              tabIndex={-1}
              className="readMore"
              href="http://www.kone.com/en/privacy-statement.aspx"
              target="_blank"
              rel="noopener noreferrer"
            >
              &nbsp;
              {t('ui-contact-privacy-statement-2')}
            </a>
            {t('ui-contact-privacy-statement-3')}
          </p>

          <ActionButton className="action-button" disabled={!canSubmit} onClick={handleSubmit}>
            {t('ui-general-submit')}
          </ActionButton>
        </>
      ) : (
        <SuccessView onClose={onClose} autoCloseSeconds={AUTO_CLOSE_SECONDS} />
      )}
    </div>
  )
}

function ActionButton(props) {
  const { className = '', children, ...rest } = props
  return (
    <button className={`ActionButton ${className}`} {...rest}>
      {children}
    </button>
  )
}

export function useFeedbackDialogAPI(options = {}, optionalFields = {}) {
  const { subject: subjectDefault = null, source = '' } = options

  const [email, setEmail] = useState('')
  const [message, setMessage] = useState('')
  const [smileySelection, setSmileySelection] = useState(null)
  const [submitting, setSubmitting] = useState(false)
  const [successfullySubmitted, setSuccessfullySubmitted] = useState(false)
  const configurationId = useSelector(selectConfigurationId)
  const designId = useSelector(selectDesignId)
  const linkInfo = useSelector(selectLinkInfo)
  const productCode = useSelector(selectProductCode)
  const localeFullCode = useSelector(selectLocaleFullCode)


  const executeRecaptcha = options.executeRecaptcha

  const validEmail = useMemo(
    () => isValidEmail(email.trim(), !optionalFields.email),
    [email, optionalFields]
  )

  // This is dumb as every field is "validated" when anything changes but no time to do a proper form handler
  // and the one that is in use in the contact form isn't any better so not using that one
  // TODO the app really could use a proper form handler like Formik or similar
  const validSmiley = optionalFields.smileySelection || !!smileySelection

  // With this it is possible to submit even in situations where a subject has been selected
  // but the message is empty. This might be undesirable but probably better than arbitrarily
  // disabling the submit button in this situation as it might be unclear to the user as to why submitting is disabled.
  // There shouldn't be any harm sending the feedback in this case anyway so not validating for that specific setup. 
  const validMessage = optionalFields.message || !!message.trim()

  const productId = getProductIDForFeedback(productCode)

  const canSubmit = useMemo(() => {
    if (successfullySubmitted) return false
    return validEmail && validSmiley && validMessage && !submitting
  }, [
    validEmail,
    validSmiley,
    validMessage,
    submitting,
    successfullySubmitted,
  ])

  async function submit() {
    if (!canSubmit) return

    try {
      setSubmitting(true)
      const recaptchaResult = await executeRecaptcha('feedback_action')
      const dataToSend = {
        email: email.trim(),
        feedbackMessage: message.trim(),
        happiness: smileySelection,
        designID: designId,
        ConfigurationID: configurationId,
        ProjectCountry: linkInfo.destinationCountry,
        URL: linkInfo.hostName,
        ProductID: productId,
        locale_full_code: localeFullCode,
        ...recaptchaResult,
      }
      // . 236324221 - Elevator planner, 909338214- ENA Elevator Planner, 261612571-Escalator planner
      console.log({ dataToSend })
      const result = await sendFeedbackForm(store,dataToSend)
      setSuccessfullySubmitted(true)
      return result
    } finally {
      setSubmitting(false)
    }
  }

  return {
    submit,
    email,
    setEmail,
    emailValid:validEmail,
    message,
    setMessage,
    smileySelection,
    setSmileySelection,
    canSubmit,
    successfullySubmitted,
  }
}
